// src/services/courseApi.js
import axios from 'axios';

const API_CONFIG = {
  baseURL:`http://34.47.145.31:8000/api/learning`,
  timeout: 15000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
};

const api = axios.create(API_CONFIG);
const DEBUG = process.env.NODE_ENV === 'development';

const logger = {
  log: (...args) => DEBUG && console.log('[CourseAPI]:', ...args),
  error: (...args) => console.error('[CourseAPI Error]:', ...args),
  warn: (...args) => console.warn('[CourseAPI Warning]:', ...args)
};

/**
 * Handles API calls with automatic retries and consistent error handling
 */
const handleApiResponse = async (apiCall, retries = 2) => {
  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await apiCall();
      logger.log('API Response:', response.config.url, response.data);

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message,
        metadata: {
          timestamp: new Date().toISOString(),
          statusCode: response.status,
          endpoint: response.config.url
        }
      };
    } catch (error) {
      lastError = error;
      logger.error(
        `Attempt ${attempt + 1}/${retries + 1} failed:`,
        error.response?.data || error.message
      );

      if (attempt < retries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  return {
    success: false,
    error: lastError.response?.data?.error || lastError.message,
    status: lastError.response?.status,
    metadata: {
      timestamp: new Date().toISOString(),
      attempts: retries + 1,
      endpoint: lastError.config?.url
    }
  };
};

export const courseApi = {
  // Course Listing and Details
  getAllCourses: (filters = {}) => handleApiResponse(() => 
    api.get('/courses', { params: filters })
  ),

  getCourseDetails: (courseId, expanded = false) => handleApiResponse(() => 
    api.get(`/courses/${courseId}`, {
      params: { expanded }
    })
  ),

  // Course Content
  getCourseContent: (courseId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/content`)
  ),

  getSectionContent: (courseId, sectionId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/sections/${sectionId}`)
  ),

  getTopicContent: (courseId, sectionId, topicId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/sections/${sectionId}/topics/${topicId}`)
  ),

  // Progress Tracking
  updateProgress: (courseId, data) => {
    if (!data.currentSection && !data.completed) {
      return Promise.reject(new Error('Either currentSection or completed status is required'));
    }

    return handleApiResponse(() => 
      api.post(`/courses/${courseId}/progress`, data)
    );
  },

  markTopicComplete: (courseId, sectionId, topicId) => handleApiResponse(() =>
    api.post(`/courses/${courseId}/sections/${sectionId}/topics/${topicId}/complete`)
  ),

  // Quiz and Assessments
  getQuiz: (courseId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/quiz`)
  ),

  submitQuiz: (courseId, answers) => {
    if (!Array.isArray(answers) || answers.length === 0) {
      return Promise.reject(new Error('Answers must be a non-empty array'));
    }

    return handleApiResponse(() => 
      api.post(`/courses/${courseId}/quiz`, { answers })
    );
  },

  // Interview Questions
  getInterviewQuestions: (courseId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/interview`)
  ),

  // Course Resources
  getResources: (courseId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/resources`)
  ),

  // Course Reviews and Ratings
  getCourseReviews: (courseId, page = 1, limit = 10) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/reviews`, {
      params: { page, limit }
    })
  ),

  submitReview: (courseId, review) => {
    if (!review.rating || !review.content) {
      return Promise.reject(new Error('Rating and content are required'));
    }

    return handleApiResponse(() => 
      api.post(`/courses/${courseId}/reviews`, review)
    );
  },

  // Course Enrollment
  enrollCourse: (courseId) => handleApiResponse(() => 
    api.post(`/courses/${courseId}/enroll`)
  ),

  unenrollCourse: (courseId) => handleApiResponse(() => 
    api.post(`/courses/${courseId}/unenroll`)
  ),

  // Course Certificates
  getCertificate: (courseId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/certificate`)
  ),

  // Course Search and Filters
  searchCourses: (query, filters = {}) => handleApiResponse(() => 
    api.get('/courses/search', {
      params: {
        q: query,
        ...filters
      }
    })
  ),

  // User Course Progress Summary
  getUserProgress: () => handleApiResponse(() => 
    api.get('/courses/progress')
  ),

  // Course Prerequisites
  checkPrerequisites: (courseId) => handleApiResponse(() => 
    api.get(`/courses/${courseId}/prerequisites/check`)
  )
};

// Request Interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request details in development
    logger.log('Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      params: config.params,
      data: config.data
    });

    return config;
  },
  (error) => {
    logger.error('Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (DEBUG) {
      const { status, config: { url }, data } = response;
      logger.log(`${status} ${url}`, data);
    }
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    // Handle rate limiting
    if (error.response?.status === 429) {
      logger.warn('Rate limit exceeded. Please try again later.');
    }

    // Log all errors
    logger.error('Response Error:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url
    });

    return Promise.reject(error);
  }
);

// Export both the API instance and the courseApi methods
export { api, courseApi as default };